/**
 * `useToolGesture` (W2-11 batch-tools; DESIGN.md §5.8, ARCHITECTURE.md §5.3): every generator sheet runs its tool
 * inside ONE live DocStore gesture, so lane, motion columns and preview show the result as ghosts while the
 * parameters change; nothing reaches history until Apply.
 *
 *  - Each new `build` (the caller memoises it on its parameters) re-runs the tool in the gesture: the previous
 *    preview is reverted and the new one applied. A tool that refuses (`tx.fail`) keeps the last valid preview on
 *    screen and its reason is returned as the outcome (the sheet shows it). `build` null = nothing to preview yet
 *    (an incomplete field): the preview is reverted.
 *  - `apply()` re-runs the latest `build` and commits ONE undoable transaction; refused applies keep the sheet open.
 *  - `cancel()` and unmounting revert the preview and leave no trace (no history entry, no journal write).
 *  - A new history label or command (a sheet switching between two tools) restarts the gesture, so the committed
 *    step always carries the current label.
 *  - Another gesture or an undo cancels a tool's gesture (the store allows one at a time); the sheet then shows
 *    "preview paused" and the next parameter change (or Apply) starts a fresh gesture on the current document.
 * The preview state lives in a small external box (useSyncExternalStore): the gesture is an external system.
 */

import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from 'react';
import type { TxBuilder } from '../../core/ops/types';
import type { MessageRef, Unsubscribe } from '../../core/util/types';
import type { AppServices } from '../../app/registry/types';
import type { DocStore, Gesture, TxnOutcome } from '../../state/types';

export interface ToolGestureOptions<R> {
  readonly services: Pick<AppServices, 'store'>;
  /** History label of the committed transaction. */
  readonly label: MessageRef;
  /** Registered command id recorded with the transaction. */
  readonly command: string;
  /** The tool over a transaction builder, or null while the parameters are incomplete. */
  readonly build: ((tx: TxBuilder) => R) | null;
}

export type ToolGestureStatus = 'idle' | 'live' | 'paused' | 'noProject';

export interface ToolGesture<R> {
  /** Outcome of the latest preview (null before the first one, or while `build` is null). */
  readonly outcome: TxnOutcome<R> | null;
  readonly status: ToolGestureStatus;
  /** Commits the latest parameters as one transaction; the outcome (null without a project or parameters). */
  apply(): TxnOutcome<R> | null;
  /** Reverts the preview. */
  cancel(): void;
}

interface Snapshot<R> {
  readonly outcome: TxnOutcome<R> | null;
  readonly status: Exclude<ToolGestureStatus, 'noProject'>;
}

interface Box<R> {
  readonly get: () => Snapshot<R>;
  readonly set: (s: Snapshot<R>) => void;
  readonly subscribe: (cb: () => void) => Unsubscribe;
}

function createBox<R>(): Box<R> {
  let snap: Snapshot<R> = { outcome: null, status: 'idle' };
  const listeners = new Set<() => void>();
  return {
    get: () => snap,
    set: (s) => {
      snap = s;
      for (const cb of listeners) cb();
    },
    subscribe: (cb) => {
      listeners.add(cb);
      return () => {
        listeners.delete(cb);
      };
    },
  };
}

interface Live {
  readonly store: DocStore;
  readonly g: Gesture;
  /** History label and command the gesture was started with (see `metaKey`). */
  readonly meta: string;
}

/** Identity of a history label and command (a MessageRef is rebuilt on every render, so compare by content). */
export function metaKey(label: MessageRef, command: string): string {
  return JSON.stringify([command, label.key, label.params ?? null]);
}

export function useToolGesture<R>(opts: ToolGestureOptions<R>): ToolGesture<R> {
  const { services, label, command, build } = opts;
  const store = services.store;
  const [box] = useState(() => createBox<R>());
  const snap = useSyncExternalStore(box.subscribe, box.get, box.get);
  const liveRef = useRef<Live | null>(null);
  const endingRef = useRef(false);
  const buildRef = useRef(build);
  const metaRef = useRef({ label, command });

  useEffect(() => {
    buildRef.current = build;
    metaRef.current = { label, command };
  });

  /**
   * The live gesture on `s`, started when there is none (or when it ended elsewhere). A gesture takes its history
   * label when it starts, so when the label or the command changed since (Bake <-> Simplify), the old preview is
   * reverted and a new gesture starts: the committed step is named after what it does.
   */
  const ensure = useCallback((s: DocStore): Gesture => {
    const cur = liveRef.current;
    const { label: l, command: c } = metaRef.current;
    const meta = metaKey(l, c);
    if (cur?.store === s && cur.g.active) {
      if (cur.meta === meta) return cur.g;
      endingRef.current = true;
      cur.g.cancel();
      endingRef.current = false;
    }
    const g = s.beginGesture({ label: l, command: c });
    liveRef.current = { store: s, g, meta };
    return g;
  }, []);

  // Preview: re-run the tool whenever the parameters (and so `build`) change.
  useEffect(() => {
    if (store === null) return;
    if (build === null) {
      const cur = liveRef.current;
      const live = cur?.store === store && cur.g.active;
      if (live) cur.g.update(() => undefined);
      box.set({ outcome: null, status: live ? 'live' : 'idle' });
      return;
    }
    const g = ensure(store);
    box.set({ outcome: g.update(build), status: 'live' });
  }, [store, build, ensure, box]);

  // A gesture cancelled elsewhere (another drag, Ctrl+Z, a load) pauses the preview.
  useEffect(() => {
    if (store === null) return undefined;
    return store.subscribe(() => {
      const cur = liveRef.current;
      if (cur !== null && !cur.g.active && !endingRef.current) {
        liveRef.current = null;
        box.set({ outcome: box.get().outcome, status: 'paused' });
      }
    });
  }, [store, box]);

  // Unmounting (sheet closed, project closed) reverts an uncommitted preview.
  useEffect(
    () => () => {
      const cur = liveRef.current;
      liveRef.current = null;
      if (cur?.g.active === true) {
        endingRef.current = true;
        cur.g.cancel();
        endingRef.current = false;
      }
    },
    [],
  );

  const apply = useCallback((): TxnOutcome<R> | null => {
    const s = services.store;
    const b = buildRef.current;
    if (s === null || b === null) return null;
    const g = ensure(s);
    const out = g.update(b);
    if (!out.ok) {
      box.set({ outcome: out, status: 'live' });
      return out;
    }
    endingRef.current = true;
    const txn = g.commit();
    endingRef.current = false;
    liveRef.current = null;
    const result: TxnOutcome<R> = { ok: true, value: out.value, txn, notes: out.notes };
    box.set({ outcome: result, status: 'idle' });
    return result;
  }, [services, ensure, box]);

  const cancel = useCallback((): void => {
    const cur = liveRef.current;
    liveRef.current = null;
    if (cur?.g.active === true) {
      endingRef.current = true;
      cur.g.cancel();
      endingRef.current = false;
    }
    box.set({ outcome: null, status: 'idle' });
  }, [box]);

  return { outcome: snap.outcome, status: store === null ? 'noProject' : snap.status, apply, cancel };
}
